import {
    useCallback, useMemo, useState,
} from 'react'

import SkyNetStepper from 'app/shared-components/SkyNetStepper'
import useBeforeUnloadDialog from 'app/hooks/useBeforeUnloadDialog'
import HistoryBlockPrompt from 'app/shared-components/HistoryBlockPrompt'
import useSimpleApiRequest from 'app/hooks/useSimpleApiRequest'
import QuoteRequests from 'app/requests/Quote/Quote.requests'

import Button from 'app/shared-components/Button'

import {
    AQuote,
} from '../AQuotes.types'
import {
    AQuoteInformationContext,
} from '../AQuoteCreate/AQuoteInformationContext'
import AQuoteInformation from '../AQuoteInformation'
import QuotationOptions from '../QuotationOptions'
import useStyles from '../AQuoteCreate/AQuoteCreate.styles'
import useAQuoteUpdate from './hooks/useAQuoteUpdate'
import getRequestData from './getRequestData'

export default function AQuoteUpdate({
    data,
    onSuccess,
    onCancel,
}: Readonly<{ data: AQuote, onSuccess: () => void, onCancel: () => void }>) {
    const {
        classes,
    } = useStyles()
    const [
        activeStep,
        setActiveStep,
    ] = useState<number>(1)

    const [
        value,
        setValue,
    ] = useState<AQuote>(data)

    const [
        isEdited,
        setIsEdited,
    ] = useState<boolean>()

    useBeforeUnloadDialog(isEdited)

    const aQuoteInformationContext = useMemo(() => {
        return {
            activeStep,
            setActiveStep,
            value,
            initialValue: data,
            setValue,
            setIsEdited,
            isEdited,
        }
    }, [
        isEdited,
        activeStep,
        value,
        data,
    ])

    const {
        mutate: updateRequestedLanes,
    } = useAQuoteUpdate(onSuccess)

    const {
        mutate: updateAQuote,
    } = useSimpleApiRequest({
        onSuccess: () => {
            setActiveStep(1)
            setIsEdited(false)
        },
        name: 'AQuoteUpdate',
        requestParams: QuoteRequests.update({
            id: value?.id,
        }),
        title: 'A-Quote was updated',
    })

    const addRequestedLanes = useCallback((aQuote: AQuote) => {
        setIsEdited(false)
        updateRequestedLanes({
            id: value?.id,
            data: getRequestData(aQuote, data),
        })
    }, [
        updateRequestedLanes,
        value?.id,
        data,
    ])

    const getActionButtons = useCallback(({
        value: formValues,
    }) => {
        return [(
            <div
                key="action-buttons"
                className={classes.buttonWrapper}
            >
                <Button
                    key="update-and-continue"
                    label="Update and Continue"
                    className={classes.button}
                    onClick={() => {
                        updateAQuote(formValues)
                    }}
                    disabled={activeStep === 1 || !isEdited}
                />
                <Button
                    key="cancel-and-continue"
                    label="Continue without Updating"
                    secondary
                    className={classes.button}
                    onClick={() => {
                        onCancel()
                        setActiveStep(1)
                        setIsEdited(false)
                    }}
                    disabled={activeStep === 1}
                />
            </div>
        )]
    }, [
        activeStep,
        classes.button,
        classes.buttonWrapper,
        isEdited,
        onCancel,
        updateAQuote,
    ])

    const stepperSteps = useMemo(() => {
        return [
            {
                key: '0',
                label: 'Fill Quote Information',
                expanded: true,
                content: <AQuoteInformation
                    key="AQuoteInformation"
                    data={value}
                    disabled={activeStep !== 0}
                    getActionButtons={getActionButtons}
                />,
            },
            {
                key: '1',
                label: 'Specify Quotation Options',
                expanded: (step: number) => {
                    return step > 0
                },
                content: <QuotationOptions
                    key="QuotationOptions"
                    data={value}
                    actionQuote={addRequestedLanes}
                />,
            },
        ]
    }, [
        activeStep,
        addRequestedLanes,
        getActionButtons,
        value,
    ])

    return (
        <AQuoteInformationContext.Provider value={aQuoteInformationContext}>
            <SkyNetStepper
                activeStep={activeStep}
                steps={stepperSteps}
            />
            <HistoryBlockPrompt
                when={isEdited}
                isPositiveAlert
                positiveLabel="Discard"
                promptText="You are about to leave this page, your progress will be lost. Would you like to discard your changes anyway?"
            />
        </AQuoteInformationContext.Provider>
    )
}
